import React from 'react';
import { FileSystemItem } from '@flowium/client';

interface FlowiumClient {
    preview(path: string, workspaceId?: string, projectId?: string): Promise<ArrayBuffer>;
}

type ThumbnailItem = Pick<FileSystemItem, 'path' | 'type' | 'preview_key' | 'workspace' | 'project'>

interface ThumbnailContext {
    workspaceId?: string;
    projectId?: string;
}

export function capitalize(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function getStockThumbnail(path: string): string {
    if (!path) return '/icons/modality/file.svg'
    const ext = path.split('.').pop()?.toLowerCase() || ''
    const modalityMap: Record<string, string[]> = {
        image: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'],
        video: ['mp4', 'mov', 'avi', 'mkv', 'webm'],
        document: ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'],
        text: ['txt', 'md', 'csv', 'json', 'xml'],
        code: ['js', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'rb', 'go', 'rs', 'sh'],
        audio: ['mp3', 'wav', 'ogg', 'flac'],
        archive: ['zip', 'rar', 'tar', 'gz', '7z'],
        binary: ['exe', 'bin', 'dll']
    }
    const stock: Record<string, string> = {
        image: '/icons/modality/image.svg',
        video: '/icons/modality/video.svg',
        document: '/icons/modality/docs.svg',
        text: '/icons/modality/text-box.svg',
        code: '/icons/modality/code.svg',
        audio: '/icons/modality/voice.svg',
        archive: '/icons/modality/archive-alt.svg',
        binary: '/icons/modality/bin.svg',
        default: '/icons/modality/file.svg'
    }
    for (const [modality, exts] of Object.entries(modalityMap)) {
        if (exts.includes(ext)) return stock[modality] || stock.default
    }
    return stock.default
}

export function getThumbnailUrl(pathName: string, isDirectory: boolean): string {
    return isDirectory ? '/icons/modality/bookmark-list.svg' : getStockThumbnail(pathName)
}

export function useThumbnailLoaderWithRetry(client: FlowiumClient | null, item?: ThumbnailItem | null, context?: ThumbnailContext): [string, boolean] {
    const pathName = item?.path ?? ''
    const isDirectory = item?.type === 'directory'
    const [thumbnailUrl, setThumbnailUrl] = React.useState(getThumbnailUrl(pathName, isDirectory))
    const [loading, setLoading] = React.useState(true)
    React.useEffect(() => {
        let cancelled = false
        let attempts = 0
        let objectUrl: string | null = null
        setThumbnailUrl(getThumbnailUrl(pathName, isDirectory))
        setLoading(true)
        const tryFetch = async () => {
            if (isDirectory || !client || !item) {
                setLoading(false)
                return
            }
            while (attempts < 3 && !cancelled) {
                try {
                    const url = await getPreviewUrl(client, item, context)
                    if (!cancelled && url) {
                        objectUrl = url
                        setThumbnailUrl(url)
                        setLoading(false)
                        return
                    }
                } catch (e) {}
                attempts++
                await new Promise(res => setTimeout(res, 3500))
            }
            if (!cancelled) {
                setLoading(false)
            }
        }
        tryFetch()
        return () => {
            cancelled = true
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl)
            }
        }
    }, [client, item, pathName, isDirectory, context?.workspaceId, context?.projectId])
    return [thumbnailUrl, loading]
}

export function isFileItem(item: FileSystemItem): item is FileSystemItem & { type: 'file' } {
    return item.type === 'file'
}

export function isDirectoryItem(item: FileSystemItem): item is FileSystemItem & { type: 'directory' } {
    return item.type === 'directory'
}

export function getNameFromPath(path: string): string {
    return path.split('/').pop() || ''
}

export const MAX_FOLDER_NAME_LENGTH = 120

export function getDisplayLabel(value: string | null | undefined, fallback: string): string {
    const normalized = (value || '').trim()
    return normalized || fallback
}

export function getDisplayNameFromPath(path: string | null | undefined, fallback = 'Untitled item'): string {
    return getDisplayLabel((path || '').split('/').pop(), fallback)
}

export function validateSingleFolderName(value: string): string | null {
    const normalized = value.trim()
    if (!normalized) return 'Folder name cannot be empty'
    if (normalized === '.' || normalized === '..') return 'Folder name cannot be "." or ".."'
    if (normalized.includes('/') || normalized.includes('\\')) return 'Folder name cannot contain slashes'
    if (normalized.length > MAX_FOLDER_NAME_LENGTH) {
        return `Folder name must be at most ${MAX_FOLDER_NAME_LENGTH} characters`
    }
    return null
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return true
        }
    } catch {
        // fall back to a temporary textarea below
    }

    try {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', 'true')
        textarea.style.position = 'fixed'
        textarea.style.left = '-9999px'
        textarea.style.top = '0'
        document.body.appendChild(textarea)
        textarea.select()
        const copied = document.execCommand('copy')
        document.body.removeChild(textarea)
        return copied
    } catch {
        return false
    }
}

export async function getPreviewUrl(client: FlowiumClient, item: ThumbnailItem, context?: ThumbnailContext): Promise<string> {
    if (item.type !== 'file') return ''
    if (!item.preview_key) return ''
    try {
        const previewBuffer = await client.preview(
            item.path,
            item.workspace || context?.workspaceId,
            item.project || context?.projectId
        )
        const blob = new Blob([previewBuffer], { type: 'image/jpeg' })
        return URL.createObjectURL(blob)
    } catch (error) {
        return ''
    }
}

export function getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
        const message = error.message;
        try {
            const parsed = JSON.parse(message) as { error?: string; message?: string };
            if (parsed.error) return parsed.error;
            if (parsed.message) return parsed.message;
        } catch {
            // fall through to the raw message
        }
        return message;
    }
    if (error && typeof error === 'object') {
        const payload = error as { error?: string; message?: string };
        if (typeof payload.error === 'string') return payload.error;
        if (typeof payload.message === 'string') return payload.message;
    }
    return String(error);
}

export function relativeTime(dateStr?: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    return date.toLocaleDateString();
}
